import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  TextField,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import { 
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Upload as UploadIcon,
  Download as DownloadIcon, 
  ExpandMore as ExpandMoreIcon,
  Help as HelpIcon,
  CheckCircle as ValidIcon,
  Cancel as InvalidIcon,
  Input as InputIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import WorkflowChat from '../WorkflowChat';
import useDocuments from '../../../hooks/useDocuments';
import useNotification from '../../../hooks/useNotification';

// Validation rule types
const VALIDATION_TYPES = [
  { value: 'required', label: 'Required' },
  { value: 'minLength', label: 'Minimum Length' },
  { value: 'maxLength', label: 'Maximum Length' },
  { value: 'pattern', label: 'Pattern (Regex)' },
  { value: 'email', label: 'Email' },
  { value: 'number', label: 'Number' },
  { value: 'min', label: 'Minimum Value' },
  { value: 'max', label: 'Maximum Value' },
  { value: 'url', label: 'URL' },
  { value: 'date', label: 'Date' },
  { value: 'custom', label: 'Custom Validation' },
];

// Field types for dropdown
const FIELD_TYPES = [
  'Text',
  'Number',
  'Email',
  'Password',
  'Date',
  'Datetime',
  'Select',
  'Multiselect',
  'Checkbox',
  'Radio',
  'Textarea',
  'File',
  'Image',
  'Phone',
  'URL',
  'Custom',
];

const FieldValidation = ({ projectId, onSave, existingContent = '' }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [fields, setFields] = useState([
    {
      id: 'field-1',
      name: 'username',
      label: 'Username',
      type: 'Text',
      description: 'Username for the account',
      validations: [
        { type: 'required', message: 'Username is required', value: true },
        { type: 'minLength', message: 'Username must be at least 3 characters', value: 3 },
        { type: 'maxLength', message: 'Username cannot exceed 20 characters', value: 20 },
      ],
      group: 'User Information'
    },
    {
      id: 'field-2',
      name: 'email',
      label: 'Email Address',
      type: 'Email',
      description: 'Email for the account',
      validations: [
        { type: 'required', message: 'Email is required', value: true },
        { type: 'email', message: 'Must be a valid email address', value: true },
      ],
      group: 'User Information'
    },
    {
      id: 'field-3',
      name: 'password',
      label: 'Password',
      type: 'Password',
      description: 'Password for the account',
      validations: [
        { type: 'required', message: 'Password is required', value: true },
        { type: 'minLength', message: 'Password must be at least 8 characters', value: 8 },
        { type: 'pattern', message: 'Password must include at least one number and one special character', value: '^(?=.*[0-9])(?=.*[!@#$%^&*])' },
      ],
      group: 'User Information'
    }
  ]);
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [activeGroup, setActiveGroup] = useState('All');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadContent, setUploadContent] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newField, setNewField] = useState({
    name: '',
    label: '',
    type: 'Text',
    description: '',
    validations: [],
    group: 'General'
  });
  const [groups, setGroups] = useState(['User Information', 'General', 'Payment', 'Profile']);
  const [newValidation, setNewValidation] = useState({
    type: 'required',
    message: '',
    value: true
  });
  
  const { showNotification } = useNotification();
  const { createDocument } = useDocuments();
  
  // Effect to parse existing content if available
  useEffect(() => {
    if (existingContent) {
      try {
        // Try to parse as JSON
        const parsedFields = JSON.parse(existingContent);
        if (Array.isArray(parsedFields)) {
          setFields(parsedFields);
          
          // Extract all unique groups
          const uniqueGroups = [...new Set(parsedFields.map(field => field.group))].filter(Boolean);
          if (uniqueGroups.length > 0) {
            setGroups([...new Set([...groups, ...uniqueGroups])]);
          }
        }
      } catch (error) {
        console.error('Error parsing existing field validation content:', error);
        // Try to extract fields from text
        extractFieldsFromText(existingContent);
      }
    }
  }, [existingContent]);
  
  const extractFieldsFromText = (text) => {
    try {
      console.log('Attempting to extract fields from text');
      
      // This is a simplified example - in a real implementation,
      // you would use more sophisticated parsing logic
      const lines = text.split('\n');
      const extractedFields = [];
      let currentField = null;
      let currentValidations = [];
      
      for (const line of lines) {
        const trimmedLine = line.trim();
        
        // Try to identify field definitions
        if (trimmedLine.startsWith('Field:') || trimmedLine.match(/^\d+\.\s+Field:/)) {
          // Save previous field if exists
          if (currentField) {
            extractedFields.push({
              ...currentField,
              validations: currentValidations
            });
          }
          
          // Start a new field
          const fieldNameMatch = trimmedLine.match(/Field:?\s+([a-zA-Z0-9_]+)/i);
          const fieldName = fieldNameMatch ? fieldNameMatch[1] : `field-${extractedFields.length + 1}`;
          
          const fieldTypeMatch = trimmedLine.match(/Type:?\s+([a-zA-Z]+)/i) || 
                               line.match(/\(([a-zA-Z]+)\)/i);
          const fieldType = fieldTypeMatch ? 
            FIELD_TYPES.find(t => t.toLowerCase() === fieldTypeMatch[1].toLowerCase()) || 'Text' : 
            'Text';
          
          currentField = {
            id: `field-${extractedFields.length + 1}`,
            name: fieldName,
            label: fieldName.charAt(0).toUpperCase() + fieldName.slice(1),
            type: fieldType,
            description: '',
            group: 'General'
          };
          
          currentValidations = [];
        }
        // Try to identify validations
        else if (currentField && (trimmedLine.includes('required') || 
                                trimmedLine.includes('validation') ||
                                trimmedLine.includes('min') ||
                                trimmedLine.includes('max'))) {
          // Extract validation type and message
          let validationType = 'custom';
          let validationValue = true;
          let validationMessage = trimmedLine;
          
          if (trimmedLine.includes('required')) {
            validationType = 'required';
          } else if (trimmedLine.match(/min[imum]?\s+length/i)) {
            validationType = 'minLength';
            const valueMatch = trimmedLine.match(/(\d+)/);
            if (valueMatch) validationValue = parseInt(valueMatch[1], 10);
          } else if (trimmedLine.match(/max[imum]?\s+length/i)) {
            validationType = 'maxLength';
            const valueMatch = trimmedLine.match(/(\d+)/);
            if (valueMatch) validationValue = parseInt(valueMatch[1], 10);
          } else if (trimmedLine.match(/pattern|regex/i)) {
            validationType = 'pattern';
          } else if (trimmedLine.match(/email/i)) {
            validationType = 'email';
          } else if (trimmedLine.match(/url/i)) {
            validationType = 'url';
          }
          
          currentValidations.push({
            type: validationType,
            message: validationMessage,
            value: validationValue
          });
        }
        // Try to extract description
        else if (currentField && !trimmedLine.startsWith('-') && trimmedLine.length > 10) {
          if (!currentField.description) {
            currentField.description = trimmedLine;
          }
        }
      }
      
      // Add the last field if exists
      if (currentField) {
        extractedFields.push({
          ...currentField,
          validations: currentValidations
        });
      }
      
      if (extractedFields.length > 0) {
        setFields(prev => [...prev, ...extractedFields]);
        showNotification({
          message: `Successfully extracted ${extractedFields.length} fields from text`,
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error extracting fields from text:', error);
    }
  };
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleEditField = (fieldId) => {
    const field = fields.find(f => f.id === fieldId);
    if (field) {
      setEditingField({...field});
      setEditingFieldId(fieldId);
    }
  };
  
  const handleUpdateField = () => {
    if (!editingField) return;
    
    const updatedFields = fields.map(field => 
      field.id === editingFieldId ? editingField : field
    );
    
    setFields(updatedFields);
    setEditingFieldId(null);
    setEditingField(null);
    
    showNotification({
      message: 'Field updated successfully',
      type: 'success'
    });
  };
  
  const handleCancelEdit = () => {
    setEditingFieldId(null);
    setEditingField(null);
  };
  
  const handleDeleteField = (fieldId) => {
    setFields(fields.filter(field => field.id !== fieldId));
    
    showNotification({
      message: 'Field deleted successfully',
      type: 'info'
    });
  };
  
  const handleAddValidation = () => {
    if (!editingField) return;
    
    // Generate message if not provided
    let message = newValidation.message;
    if (!message) {
      switch (newValidation.type) {
        case 'required':
          message = `${editingField.label} is required`;
          break;
        case 'minLength':
          message = `${editingField.label} must be at least ${newValidation.value} characters`;
          break;
        case 'maxLength':
          message = `${editingField.label} cannot exceed ${newValidation.value} characters`;
          break;
        case 'pattern':
          message = `${editingField.label} has an invalid format`;
          break;
        case 'email':
          message = `Please enter a valid email address`;
          break;
        default:
          message = `Validation failed for ${editingField.label}`;
      }
    }
    
    setEditingField({
      ...editingField,
      validations: [
        ...editingField.validations,
        {
          ...newValidation,
          message
        }
      ]
    });
    
    // Reset new validation form
    setNewValidation({
      type: 'required',
      message: '',
      value: true
    });
  };
  
  const handleDeleteValidation = (index) => {
    if (!editingField) return;
    
    const updatedValidations = editingField.validations.filter((_, i) => i !== index);
    
    setEditingField({
      ...editingField,
      validations: updatedValidations
    });
  };
  
  const handleCreateField = () => {
    // Basic validation
    if (!newField.name || !newField.label) {
      showNotification({
        message: 'Field name and label are required',
        type: 'warning'
      });
      return;
    }
    
    // Generate ID
    const fieldId = `field-${fields.length + 1}`;
    
    // Add new field to the list
    setFields([
      ...fields,
      {
        ...newField,
        id: fieldId
      }
    ]);
    
    // Reset form and close dialog
    setNewField({
      name: '',
      label: '',
      type: 'Text',
      description: '',
      validations: [],
      group: 'General'
    });
    setCreateDialogOpen(false);
    
    showNotification({
      message: 'Field created successfully',
      type: 'success'
    });
  };
  
  const handleAddGroup = (groupName) => {
    if (!groupName || groups.includes(groupName)) return;
    
    setGroups([...groups, groupName]);
  };
  
  const saveFieldValidation = async () => {
    try {
      setLoading(true);
      
      // Convert fields to JSON for storage
      const fieldsData = JSON.stringify(fields, null, 2);
      
      // Save fields as document
      if (projectId) {
        const document = await createDocument(projectId, {
          title: 'Field Validation Rules',
          content: fieldsData,
          type: 'field-validation'
        });
        
        showNotification({
          message: 'Field validation rules saved successfully',
          type: 'success'
        });
        
        // Notify parent component
        if (onSave) {
          onSave(fieldsData);
        }
      }
    } catch (error) {
      console.error('Error saving field validation:', error);
      showNotification({
        message: `Error saving field validation: ${error.message}`,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  const generateFieldValidationMarkdown = () => {
    let markdown = `# Field Validation Rules\n\n`;
    
    // Group fields by their group
    const fieldsByGroup = {};
    fields.forEach(field => {
      const group = field.group || 'Ungrouped';
      if (!fieldsByGroup[group]) {
        fieldsByGroup[group] = [];
      }
      fieldsByGroup[group].push(field);
    });
    
    // Generate markdown for each group
    Object.entries(fieldsByGroup).forEach(([group, groupFields]) => {
      markdown += `## ${group}\n\n`;
      
      groupFields.forEach(field => {
        markdown += `### ${field.label} (${field.name})\n`;
        markdown += `- **Type:** ${field.type}\n`;
        if (field.description) {
          markdown += `- **Description:** ${field.description}\n`;
        }
        
        if (field.validations && field.validations.length > 0) {
          markdown += `- **Validations:**\n`;
          field.validations.forEach(validation => {
            markdown += `  - ${validation.type}: ${validation.message}`;
            if (validation.value !== true && validation.value !== undefined) {
              markdown += ` (Value: ${validation.value})`;
            }
            markdown += `\n`;
          });
        }
        
        markdown += `\n`;
      });
    });
    
    return markdown;
  };
  
  const exportFieldValidation = () => {
    const markdown = generateFieldValidationMarkdown();
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    
    // Create a link and trigger download
    const a = document.createElement('a');
    a.href = url;
    a.download = 'field-validation-rules.md';
    a.click();
    
    URL.revokeObjectURL(url);
  };
  
  const handleUploadFieldValidation = () => {
    if (!uploadContent.trim()) {
      showNotification({
        message: 'Please enter field validation content to upload',
        type: 'warning'
      });
      return;
    }
    
    try {
      // Try to parse as JSON first
      try {
        const parsedFields = JSON.parse(uploadContent);
        if (Array.isArray(parsedFields)) {
          setFields(parsedFields);
          
          // Extract all unique groups
          const uniqueGroups = [...new Set(parsedFields.map(field => field.group))].filter(Boolean);
          if (uniqueGroups.length > 0) {
            setGroups([...new Set([...groups, ...uniqueGroups])]);
          }
          
          setUploadDialogOpen(false);
          
          showNotification({
            message: `Successfully loaded ${parsedFields.length} fields`,
            type: 'success'
          });
          return;
        } else {
          throw new Error('Uploaded JSON is not an array of fields');
        }
      } catch (jsonError) {
        console.log('Not valid JSON, trying to extract from text');
      }
      
      // If not valid JSON, try to extract from text
      extractFieldsFromText(uploadContent);
      setUploadDialogOpen(false);
    } catch (error) {
      console.error('Error uploading field validation:', error);
      showNotification({
        message: `Error uploading field validation: ${error.message}`,
        type: 'error'
      });
    }
  };
  
  // Filter fields by active group
  const filteredFields = activeGroup === 'All' 
    ? fields 
    : fields.filter(field => field.group === activeGroup);

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Field Validation Rules</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            startIcon={<UploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
          >
            Import
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<DownloadIcon />}
            onClick={exportFieldValidation}
          >
            Export
          </Button>
          <Button 
            variant="contained" 
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={loading}
            onClick={saveFieldValidation}
          >
            Save Validation Rules
          </Button>
        </Box>
      </Box>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label="Fields" />
        <Tab label="Create Field" />
        <Tab label="Preview" />
        <Tab label="Help" />
      </Tabs>

      {activeTab === 0 && (
        <Box>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant={activeGroup === 'All' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setActiveGroup('All')}
              >
                All
              </Button>
              {groups.map(group => (
                <Button
                  key={group}
                  variant={activeGroup === group ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setActiveGroup(group)}
                >
                  {group}
                </Button>
              ))}
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Create Field
            </Button>
          </Box>
          
          {filteredFields.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Field Name</TableCell>
                    <TableCell>Label</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Group</TableCell>
                    <TableCell>Validations</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredFields.map((field) => (
                    <TableRow key={field.id}>
                      <TableCell>{field.name}</TableCell>
                      <TableCell>{field.label}</TableCell>
                      <TableCell>
                        <Chip label={field.type} size="small" />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={field.group || 'General'} 
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {field.validations.map((validation, index) => (
                            <Tooltip
                              key={index}
                              title={validation.message}
                              arrow
                            >
                              <Chip 
                                label={validation.type} 
                                size="small"
                                color={validation.type === 'required' ? 'primary' : 'default'}
                                variant="outlined"
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleEditField(field.id)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDeleteField(field.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 4, 
                textAlign: 'center',
                bgcolor: 'background.paper'
              }}
            >
              <InputIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Fields Defined
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Create your first field validation rule to get started
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
              >
                Create Field
              </Button>
            </Paper>
          )}
          
          {/* Field Edit Dialog */}
          {editingField && (
            <Dialog
              open={!!editingFieldId}
              onClose={handleCancelEdit}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>Edit Field: {editingField.label}</DialogTitle>
              <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Field Name"
                      value={editingField.name}
                      onChange={(e) => setEditingField({ ...editingField, name: e.target.value })}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Field Label"
                      value={editingField.label}
                      onChange={(e) => setEditingField({ ...editingField, label: e.target.value })}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Field Type</InputLabel>
                      <Select
                        value={editingField.type}
                        onChange={(e) => setEditingField({ ...editingField, type: e.target.value })}
                        label="Field Type"
                      >
                        {FIELD_TYPES.map((type) => (
                          <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Field Group</InputLabel>
                      <Select
                        value={editingField.group || 'General'}
                        onChange={(e) => setEditingField({ ...editingField, group: e.target.value })}
                        label="Field Group"
                      >
                        {groups.map((group) => (
                          <MenuItem key={group} value={group}>{group}</MenuItem>
                        ))}
                        <MenuItem value="__new">
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <AddIcon fontSize="small" sx={{ mr: 1 }} />
                            Add New Group
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={editingField.description}
                      onChange={(e) => setEditingField({ ...editingField, description: e.target.value })}
                      multiline
                      rows={2}
                      margin="normal"
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                      Validation Rules
                    </Typography>
                    
                    {editingField.validations.length > 0 ? (
                      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Type</TableCell>
                              <TableCell>Message</TableCell>
                              <TableCell>Value</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {editingField.validations.map((validation, index) => (
                              <TableRow key={index}>
                                <TableCell>{validation.type}</TableCell>
                                <TableCell>{validation.message}</TableCell>
                                <TableCell>
                                  {validation.value === true ? 'Yes' : 
                                   validation.value === false ? 'No' : 
                                   validation.value}
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteValidation(index)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No validation rules defined
                      </Typography>
                    )}
                    
                    <Accordion variant="outlined">
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Add Validation Rule</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                              <InputLabel>Validation Type</InputLabel>
                              <Select
                                value={newValidation.type}
                                onChange={(e) => setNewValidation({ 
                                  ...newValidation, 
                                  type: e.target.value,
                                  value: e.target.value === 'required' || 
                                         e.target.value === 'email' || 
                                         e.target.value === 'url' ? true : 
                                         (e.target.value === 'minLength' || 
                                          e.target.value === 'maxLength' ? 0 : '')
                                })}
                                label="Validation Type"
                              >
                                {VALIDATION_TYPES.map((type) => (
                                  <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          
                          {(newValidation.type === 'minLength' || 
                            newValidation.type === 'maxLength' || 
                            newValidation.type === 'min' || 
                            newValidation.type === 'max' || 
                            newValidation.type === 'pattern') && (
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="Value"
                                value={newValidation.value}
                                onChange={(e) => setNewValidation({ 
                                  ...newValidation, 
                                  value: ['minLength', 'maxLength', 'min', 'max'].includes(newValidation.type) ? 
                                    Number(e.target.value) : e.target.value
                                })}
                              />
                            </Grid>
                          )}
                          
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Error Message"
                              value={newValidation.message}
                              onChange={(e) => setNewValidation({ ...newValidation, message: e.target.value })}
                              placeholder={`Leave blank to auto-generate (e.g., "${editingField.label} is required")`}
                            />
                          </Grid>
                          
                          <Grid item xs={12}>
                            <Button
                              variant="contained"
                              onClick={handleAddValidation}
                              startIcon={<AddIcon />}
                              fullWidth
                            >
                              Add Validation Rule
                            </Button>
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCancelEdit}>Cancel</Button>
                <Button 
                  variant="contained" 
                  onClick={handleUpdateField}
                  startIcon={<SaveIcon />}
                >
                  Save Changes
                </Button>
              </DialogActions>
            </Dialog>
          )}
        </Box>
      )}

      {activeTab === 1 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Create Field</Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Define a new field and its validation rules
          </Typography>
          
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Field Name"
                  value={newField.name}
                  onChange={(e) => setNewField({ ...newField, name: e.target.value })}
                  helperText="Technical name, used in code (e.g., firstName)"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Field Label"
                  value={newField.label}
                  onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                  helperText="Display name shown to users (e.g., First Name)"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Field Type</InputLabel>
                  <Select
                    value={newField.type}
                    onChange={(e) => setNewField({ ...newField, type: e.target.value })}
                    label="Field Type"
                  >
                    {FIELD_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>{type}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Field Group</InputLabel>
                  <Select
                    value={newField.group}
                    onChange={(e) => {
                      if (e.target.value === '__new') {
                        const newGroup = prompt('Enter new group name:');
                        if (newGroup && newGroup.trim()) {
                          handleAddGroup(newGroup.trim());
                          setNewField({ ...newField, group: newGroup.trim() });
                        }
                      } else {
                        setNewField({ ...newField, group: e.target.value });
                      }
                    }}
                    label="Field Group"
                  >
                    {groups.map((group) => (
                      <MenuItem key={group} value={group}>{group}</MenuItem>
                    ))}
                    <MenuItem value="__new">
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AddIcon fontSize="small" sx={{ mr: 1 }} />
                        Add New Group
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={newField.description}
                  onChange={(e) => setNewField({ ...newField, description: e.target.value })}
                  multiline
                  rows={2}
                  helperText="Explanation of the field's purpose"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom>
                  Common Validation Rules
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Select validation rules to add to this field
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => {
                        // Add required validation
                        setNewField({
                          ...newField,
                          validations: [
                            ...newField.validations,
                            {
                              type: 'required',
                              message: `${newField.label} is required`,
                              value: true
                            }
                          ]
                        });
                      }}
                      startIcon={<ValidIcon fontSize="small" />}
                    >
                      Required Field
                    </Button>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => {
                        // Add email validation
                        setNewField({
                          ...newField,
                          validations: [
                            ...newField.validations,
                            {
                              type: 'email',
                              message: `Please enter a valid email address`,
                              value: true
                            }
                          ]
                        });
                      }}
                      disabled={newField.type !== 'Email' && newField.type !== 'Text'}
                    >
                      Email Validation
                    </Button>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => {
                        // Add length validation
                        setNewField({
                          ...newField,
                          validations: [
                            ...newField.validations,
                            {
                              type: 'minLength',
                              message: `${newField.label} must be at least 3 characters`,
                              value: 3
                            }
                          ]
                        });
                      }}
                      disabled={!['Text', 'Password', 'Textarea'].includes(newField.type)}
                    >
                      Min Length (3)
                    </Button>
                  </Grid>
                </Grid>
                
                <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                  Current Validation Rules
                </Typography>
                
                {newField.validations.length > 0 ? (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    {newField.validations.map((validation, index) => (
                      <Box 
                        key={index} 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          py: 1,
                          borderBottom: index < newField.validations.length - 1 ? '1px solid #f0f0f0' : 'none'
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle2">
                            {VALIDATION_TYPES.find(t => t.value === validation.type)?.label || validation.type}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {validation.message}
                            {validation.value !== true && validation.value !== undefined && (
                              <Typography component="span" color="primary.main">
                                {' '}(Value: {validation.value})
                              </Typography>
                            )}
                          </Typography>
                        </Box>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => {
                            const updatedValidations = newField.validations.filter((_, i) => i !== index);
                            setNewField({
                              ...newField,
                              validations: updatedValidations
                            });
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Paper>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No validation rules added yet
                  </Typography>
                )}
              </Grid>
              
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateField}
                  size="large"
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  Create Field
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      )}

      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="subtitle1" gutterBottom>Field Validation Preview</Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Typography variant="subtitle2" gutterBottom>
                Groups Summary
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {groups.map(group => (
                  <Chip 
                    key={group}
                    label={`${group} (${fields.filter(f => f.group === group).length})`}
                    color="primary"
                    variant="outlined"
                    onClick={() => setActiveGroup(group)}
                  />
                ))}
              </Box>
              
              <Typography variant="subtitle2" gutterBottom>
                Validation Types
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {VALIDATION_TYPES.map(type => {
                  const count = fields.flatMap(f => f.validations).filter(v => v.type === type.value).length;
                  return count > 0 ? (
                    <Chip 
                      key={type.value}
                      label={`${type.label} (${count})`}
                      variant="outlined"
                    />
                  ) : null;
                })}
              </Box>
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="subtitle1" gutterBottom>
                Markdown Preview
              </Typography>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  maxHeight: '400px', 
                  overflow: 'auto',
                  bgcolor: '#f5f5f5',
                  fontFamily: 'monospace'
                }}
              >
                <pre>{generateFieldValidationMarkdown()}</pre>
              </Paper>
            </Paper>
          </Grid>
          
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="subtitle1" gutterBottom>Form Field Preview</Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Box sx={{ maxWidth: 600, mx: 'auto', p: 2, border: '1px dashed #ccc', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Sample Form
                </Typography>
                
                <Grid container spacing={2}>
                  {fields.map((field) => (
                    <Grid item xs={12} key={field.id}>
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          {field.label}
                          {field.validations.some(v => v.type === 'required') && (
                            <Typography component="span" color="error.main" sx={{ ml: 0.5 }}>*</Typography>
                          )}
                        </Typography>
                        
                        {['Text', 'Email', 'Password', 'Number', 'URL', 'Phone'].includes(field.type) && (
                          <TextField
                            fullWidth
                            placeholder={`Enter ${field.label.toLowerCase()}`}
                            type={field.type.toLowerCase()}
                            size="small"
                          />
                        )}
                        
                        {field.type === 'Textarea' && (
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            placeholder={`Enter ${field.label.toLowerCase()}`}
                            size="small"
                          />
                        )}
                        
                        {field.type === 'Select' && (
                          <FormControl fullWidth size="small">
                            <InputLabel>{`Select ${field.label}`}</InputLabel>
                            <Select label={`Select ${field.label}`}>
                              <MenuItem value="option1">Option 1</MenuItem>
                              <MenuItem value="option2">Option 2</MenuItem>
                              <MenuItem value="option3">Option 3</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                        
                        {field.type === 'Date' && (
                          <TextField
                            fullWidth
                            type="date"
                            size="small"
                          />
                        )}
                        
                        {field.validations.length > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            <InfoIcon fontSize="inherit" sx={{ fontSize: '0.75rem', mr: 0.5, verticalAlign: 'middle' }} />
                            {field.validations.map(v => v.message).join(', ')}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  ))}
                  
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Button variant="contained">Submit</Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
      
      {activeTab === 3 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Field Validation Help</Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <WorkflowChat
                sectionName="Field Validation"
                projectId={projectId}
                placeholder="Ask about field validation best practices..."
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Validation Guidelines</Typography>
                <Typography variant="body2" paragraph>
                  • Use clear, specific error messages that tell users how to fix the issue
                </Typography>
                <Typography variant="body2" paragraph>
                  • Validate both on the client-side for immediate feedback and server-side for security
                </Typography>
                <Typography variant="body2" paragraph>
                  • Mark required fields clearly with visual indicators (e.g., asterisks)
                </Typography>
                <Typography variant="body2" paragraph>
                  • For complex validations, consider using a validation library
                </Typography>
                <Typography variant="body2" paragraph>
                  • Consider accessibility when displaying validation errors
                </Typography>
                <Typography variant="body2" paragraph>
                  • Group related fields logically for better organization
                </Typography>
                <Typography variant="body2" paragraph>
                  • Test your validation rules with edge cases
                </Typography>
                <Typography variant="body2" paragraph>
                  • Use appropriate validation types for different field types (e.g., email validation for email fields)
                </Typography>
              </Paper>
              
              <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>Common Validation Types</Typography>
                
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Required Field</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2">
                      Ensures that a field is not empty before form submission. This is the most basic validation rule.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
                
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Length Validation</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2">
                      MinLength and MaxLength validations ensure text input meets length requirements. 
                      Useful for usernames, passwords, and other text fields with specific length constraints.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
                
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Pattern Validation</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2">
                      Uses regular expressions to validate that input follows a specific pattern.
                      Useful for complex validation like passwords with special character requirements.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
                
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Type Validation</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2">
                      Specialized validations like email, URL, and date ensure that input is properly formatted
                      for the specific data type required.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
      
      {/* Create Field Dialog - Simple version for quick creation */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Field</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Field Name"
                value={newField.name}
                onChange={(e) => setNewField({ ...newField, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Field Label"
                value={newField.label}
                onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Field Type</InputLabel>
                <Select
                  value={newField.type}
                  onChange={(e) => setNewField({ ...newField, type: e.target.value })}
                  label="Field Type"
                >
                  {FIELD_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Field Group</InputLabel>
                <Select
                  value={newField.group}
                  onChange={(e) => {
                    if (e.target.value === '__new') {
                      const newGroup = prompt('Enter new group name:');
                      if (newGroup && newGroup.trim()) {
                        handleAddGroup(newGroup.trim());
                        setNewField({ ...newField, group: newGroup.trim() });
                      }
                    } else {
                      setNewField({ ...newField, group: e.target.value });
                    }
                  }}
                  label="Field Group"
                >
                  {groups.map((group) => (
                    <MenuItem key={group} value={group}>{group}</MenuItem>
                  ))}
                  <MenuItem value="__new">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AddIcon fontSize="small" sx={{ mr: 1 }} />
                      Add New Group
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={newField.description}
                onChange={(e) => setNewField({ ...newField, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained"
            onClick={handleCreateField}
            startIcon={<AddIcon />}
            disabled={!newField.name || !newField.label}
          >
            Create Field
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Upload Field Validation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph sx={{ mt: 2 }}>
            Paste your field validation details as JSON or in text format below. 
            The system will attempt to parse and organize the information.
          </Typography>
          
          <TextField
            fullWidth
            multiline
            rows={12}
            placeholder="Paste your field validation JSON or text here..."
            value={uploadContent}
            onChange={(e) => setUploadContent(e.target.value)}
            variant="outlined"
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUploadFieldValidation}>Process</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default FieldValidation;